import { isValid } from 'date-fns';
import * as React from 'react';
import { useStyles, useBoolean, usePositioner, useFormControlContext, useKeyPress, useMergedRef } from '@sravni/react-utils';
import { Z_INDEXES } from '../../../constants/zIndexes.js';
import { getDateFormatted } from '../../Calendar/utils/date.js';
import { MaskInput } from '../../MaskInput/src/MaskInput.js';
import modules_640a77b2 from '../styles/DateInput.module.less.js';
import { CalendarView } from './CalendarView.js';

const isDateFormatRange = (dateFormat) => typeof dateFormat === 'object';
const isDate = (value) => isValid(value);
const isDateRange = (value) => !!value && 'from' in value && 'to' in value;
const formatDate = (strArr) => strArr.split(/[-./]/).reverse();
/**
 * @description To select or input a date
 *
 * @component
 * @example
 * ```jsx
 * <DateInput />
 * ```
 */
const DateInput = React.forwardRef((props, ref) => {
    const { defaultValue, value, autoFocus, mode = 'single', defaultDate, maxDate, minDate, disabledIntervals, rangeLimit, dateFormat, invalid, disabled, readOnly, placement = 'auto-start', className, mask, userInput = false, onClick, onChange, onSelect, onFocus, onBlur, ...restProps } = props;
    const cx = useStyles(modules_640a77b2);
    const classNames = cx(className, 'container');
    const calendarWrapper = cx('calendar-wrapper');
    const reference = React.useRef(null);
    const inputRef = React.useRef(null);
    const calendarRef = React.useRef(null);
    const popper = React.useRef(null);
    const isFirstRender = React.useRef(true);
    const [visible, setVisible] = useBoolean(false);
    const [focused, setFocused] = useBoolean(false);
    const { getReferenceProps, getPopperProps } = usePositioner({
        placement,
        offset: [0, 8],
    });
    const [innerValue, setInnerValue] = React.useState(value || defaultValue);
    const formControlContext = useFormControlContext();
    const [displayValue, setDisplayValue] = React.useState();
    const popperProps = React.useMemo(() => ({ ...getPopperProps({ style: { zIndex: Z_INDEXES.DROPDOWN } }, popper) }), [getPopperProps]);
    const innerMask = React.useMemo(() => {
        if (!userInput) {
            return '';
        }
        return mask ?? '99.99.9999';
    }, [userInput, mask]);
    const updateValue = React.useCallback(() => {
        if (mode === 'range') {
            setDisplayValue(isDateRange(innerValue) && (isDateFormatRange(dateFormat) || !dateFormat)
                ? `${getDateFormatted(innerValue.from, dateFormat?.from)} — ${getDateFormatted(innerValue.to, dateFormat?.to)}`
                : undefined);
            return;
        }
        if (mode === 'single' && !isDateFormatRange(dateFormat)) {
            setDisplayValue(isDate(innerValue) ? getDateFormatted(innerValue, dateFormat) : undefined);
            return;
        }
        setDisplayValue(undefined);
    }, [innerValue, dateFormat, mode]);
    const handleChangeSelected = React.useCallback((range) => {
        onChange?.(range);
        onSelect?.(range);
        setInnerValue(range);
        setVisible.off();
    }, [onChange, onSelect, setVisible]);
    const handleClick = React.useCallback((event) => {
        if (disabled)
            return;
        !readOnly && setVisible.toggle();
        onClick?.(event);
    }, [disabled, readOnly, setVisible, onClick]);
    const handleFocus = React.useCallback((event) => {
        if (!focused) {
            setFocused.on();
            onFocus?.(event);
        }
    }, [focused, onFocus, setFocused]);
    const handleBlur = React.useCallback((event) => {
        if (event.relatedTarget === calendarRef.current || calendarRef.current?.contains(event.relatedTarget))
            return;
        if (userInput) {
            updateValue();
        }
        setVisible.off();
        onBlur?.(event);
    }, [onBlur, updateValue, setVisible, userInput]);
    const handleChange = React.useCallback((value) => {
        if (!value) {
            return;
        }
        const date = formatDate(value);
        const parsedDate = Date.parse(`${date[0]}/${date[1]}/${date[2]}`);
        const newDate = new Date(Number(date[0]), Number(date[1]) - 1, Number(date[2]));
        if (parsedDate && isValid(newDate)) {
            setInnerValue(newDate);
            onChange?.(newDate);
            return;
        }
        setDisplayValue(value);
    }, [onChange]);
    useKeyPress({
        enabled: visible || focused,
        keyMap: {
            Escape: (event) => {
                event.preventDefault();
                setVisible.off();
                inputRef.current?.focus();
            },
            Tab: () => {
                setVisible.off();
                setFocused.off();
            },
            Enter: () => {
                if (!userInput) {
                    return;
                }
                updateValue();
                setVisible.off();
            },
        },
    }, [visible, focused, userInput, updateValue]);
    React.useEffect(() => {
        if (!isFirstRender.current) {
            setInnerValue(value);
        }
        else {
            isFirstRender.current = false;
        }
    }, [value]);
    React.useEffect(() => {
        autoFocus && inputRef.current && inputRef.current.focus();
    }, [autoFocus]);
    React.useEffect(() => {
        const listener = (event) => {
            if (reference.current?.contains(event.target)) {
                return;
            }
            if (popper.current?.contains(event.target)) {
                return;
            }
            setVisible.off();
            setFocused.off();
        };
        document.addEventListener('mousedown', listener);
        return () => {
            document.removeEventListener('mousedown', listener);
        };
    }, [reference, popper, setVisible, setFocused]);
    React.useEffect(() => {
        updateValue();
    }, [updateValue]);
    return (React.createElement("div", { "data-qa": "DateInput", className: classNames },
        React.createElement("div", { ...getReferenceProps({}, reference), onClick: handleClick },
            React.createElement(MaskInput, { ...restProps, mask: innerMask, ref: useMergedRef(ref, inputRef), value: displayValue, invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, readOnly: !userInput, onFocus: handleFocus, onBlur: handleBlur, onChange: handleChange })),
        React.createElement(CalendarView, { visible: visible, calendarWrapper: calendarWrapper, popperProps: popperProps, defaultDate: defaultDate, maxDate: maxDate, minDate: minDate, disabledIntervals: disabledIntervals, rangeLimit: rangeLimit, calendarRef: calendarRef, mode: mode, value: innerValue, handleChangeSelected: handleChangeSelected })));
});
DateInput.displayName = 'DateInput';

export { DateInput };
//# sourceMappingURL=DateInput.js.map
